<script lang="ts" setup>
import {computed, defineAsyncComponent, ref, watch} from "vue";

import useStore from "@src/stores/store";

import MessagePreview from "@src/components/views/HomeView/MainArea/ChatArea/Message/MessagePreview.vue";
import {useChatSessionStore} from "@src/stores/chatSession";
import {useMessageStore} from "@src/stores/messages";
const MessageTemplates = defineAsyncComponent(() => import("@src/components/views/HomeView/MainArea/FooterArea/MessageTemplates/MessageTemplates.vue"));
import {useAudioStore} from "@src/stores/audio";
import MessageService from '@src/services/message'
import GroupMessageService from "@src/services/groupMessage";
import GroupMessage from "@src/types/GroupMessage";
import SessionService from '@src/services/session'
import Button from "@src/components/ui/inputs/Button.vue";
import {useWAAPIStore} from "@src/stores/waapi";
const WaapiCampaign = defineAsyncComponent(() => import("@src/components/shared/modals/WaapiCampaignModal/WaapiCampaign.vue"));
import InputArea from "@src/components/views/HomeView/MainArea/FooterArea/InputArea.vue";
import EditInputArea from "@src/components/views/HomeView/MainArea/FooterArea/EditInputArea.vue";
import ForwardMessagesBar from "@src/components/views/HomeView/MainArea/FooterArea/ForwardMessagesBar.vue";
const ForwardSessionsModal = defineAsyncComponent(() => import("@src/components/shared/modals/ForwardSessionModal/ForwardSessionModal.vue"));
import {useForwardMessages} from "@src/composables/useForwardMessages";
import {permissionsHelper} from "@src/helpers/permissionsHelper";
import {useIsMobile} from "@src/composables/useIsMobile";

const store = useStore();
const messageStore = useMessageStore()
const sessionStore = useChatSessionStore()
const audioStore = useAudioStore()
const waapiStore = useWAAPIStore()
const {handleConfirmForward} = useForwardMessages();
const {isMobile} = useIsMobile();

const loading = ref(false);
const inputAreaRef = ref<InstanceType<typeof InputArea> | null>(null);

const isSession = computed(() => sessionStore.type === "session");
const selected = computed(() => sessionStore.selectedSession as any);
const isWaapiSession = computed(() => sessionStore.selectedSession?.channel?.type === 'waapi');
const isExpired = computed(() => isWaapiSession.value && sessionStore.selectedSession?.is_expired && sessionStore.selectedSession?.channel?.status === 'connected')
const canSendCampaign = computed(() => permissionsHelper.get('campaign_create'))

const channelConnected = computed(() => {
    if (!isSession.value) return true;
    const st = String(selected.value?.channel?.status || "");
    return st === "connected" || st === "BOT_STATUS_CONNECTED";
});

const canSend = computed(() => {
    if (!isSession.value) return true;
    return channelConnected.value && !sessionStore.selectedSession?.is_expired;
});

const sendMessage = async () => {
    if (!canSend.value) return false;

    if (messageStore.message.length === 0) {
        return false;
    }

    const reply_to = (sessionStore.replyMessage?.id as number) || null
    const text = messageStore.message;

    if (sessionStore.type === "session") {
        const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
        const tempId = 'temp-' + Array.from({length: 7}, () => letters.charAt(Math.floor(Math.random() * letters.length))).join("");
        messageStore.message = ''
        sessionStore.replyMessage = undefined;

        setTimeout(async () => {
            sessionStore.scrollDown();
        }, 0);

        await MessageService.sendMessage(text, tempId, reply_to)
        return;
    }

    const group = sessionStore.selectedSession as any;
    if (!group?.id) return;

    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const tempMessage: GroupMessage = {
        id: tempId,
        group_id: group.id,
        user_id: store.user?.id ?? null,
        costumer_id: null,
        token: "",
        type: "text",
        content: text,
        reply_to: reply_to ? Number(reply_to) : null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        selected: false,
        sender: {
            type: "user",
            id: store.user?.id ?? 0,
            name: store.user?.name ?? "",
            avatar: store.user?.profile_photo_path ?? store.user?.avatar ?? "",
        },
        user: {
            id: store.user?.id ?? 0,
            name: store.user?.name ?? "",
            profile_photo_path: store.user?.profile_photo_path ?? store.user?.avatar ?? "",
        },
    };

    messageStore.groupMessages.push(tempMessage);

    messageStore.message = "";
    sessionStore.replyMessage = undefined;
    sessionStore.scrollDown();

    const res = await GroupMessageService.sendText(group.id, text, reply_to, tempId);

    // no fluxo correto, quem substitui a temp pela real é o websocket
    if (!res?.success) {
        messageStore.groupMessages = messageStore.groupMessages.filter(
            (m: GroupMessage) => String(m.id) !== String(tempId)
        );
        return;
    }

    setTimeout(async () => {
        sessionStore.scrollDown();
    }, 0);
}

function handleKeydown(event: KeyboardEvent) {
    if (messageStore.messageTemplatesModal) {
        if (event.key === 'Esc') {
            messageStore.messageTemplatesModal = false;
        }

        const templates = messageStore.filteredTemplates || [];

        if (event.key === 'ArrowDown') {
            event.preventDefault();

            const currentIndex = templates.findIndex(
                (t: any) => t.id === messageStore.selectedTemplate
            );

            const nextIndex = currentIndex < templates.length - 1 ? currentIndex + 1 : 0;

            messageStore.selectedTemplate = templates[nextIndex]?.id;
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();

            const currentIndex = templates.findIndex(
                (t: any) => t.id === messageStore.selectedTemplate
            );

            const prevIndex = currentIndex > 0 ? currentIndex - 1 : templates.length - 1;

            messageStore.selectedTemplate = templates[prevIndex]?.id;
            return;
        }
    }

    if (event.key === 'Enter' && messageStore.messageTemplatesModal) {
        event.preventDefault();

        const templates = messageStore.filteredTemplates || [];

        const template = templates.find(
            (t: any) => t.id === messageStore.selectedTemplate
        );

        if (template) {
            if (messageStore.message === '/' + messageStore.templateSearch) {
                messageStore.message = '';
            }

            messageStore.message += template.message.replace(/{NOME}/g, sessionStore.selectedSession?.costumer?.name ?? '');
            messageStore.templateSearch = '';
            messageStore.messageTemplatesModal = false;
        }

        return;
    } else if (event.key === 'Enter' && event.shiftKey) {
        return;
    }

    if (event.key === '/' && messageStore.message === '') {
        messageStore.messageTemplatesModal = true;
        messageStore.templateSearch = '';
        return;
    }

    if (messageStore.messageTemplatesModal) {
        if (event.key === 'Backspace') {
            if (messageStore.message === '/') {
                messageStore.messageTemplatesModal = false;
                return;
            }
            messageStore.templateSearch = messageStore.templateSearch.slice(0, -1);
            return;
        }

        if (event.key === ' ') {
            event.preventDefault();
            messageStore.templateSearch += '-';
            messageStore.message += '-'
            return;
        }

        if (event.key.length === 1) {
            messageStore.templateSearch += event.key;
            return;
        }

        if (event.key === 'Escape') {
            messageStore.templateSearch = '';
            messageStore.messageTemplatesModal = false;
        }
    }

    if (event.key === 'Enter') {
        if (isMobile.value) {
            return;
        }
        event.preventDefault();
        sendMessage();
    }
}

const initSession = async () => {
    const session = sessionStore.selectedSession
    if (!session) return;

    loading.value = true;
    const success = await SessionService.initSession(session)
    loading.value = false;

    if (success) {
        store.activeSidebarComponent = 'active'
        session.status = "active";
        await sessionStore.selectSession(session);
    }
}

watch(() => sessionStore.replyMessage, () => {
    inputAreaRef.value?.textareaRef?.focus();
})
</script>

<template>
    <div class="w-full">
        <Transition v-if="sessionStore.forwardMode" appear name="forward-bar">
            <ForwardMessagesBar/>
        </Transition>

        <template v-else>
            <Transition
                enter-active-class="transition-all duration-300 ease-out"
                enter-from-class="opacity-0 translate-y-2"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition-all duration-200 ease-in"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-2"
            >
                <div
                    v-if="sessionStore.replyMessage && sessionStore.selectedSession"
                    class="relative transition-all duration-200"
                >
                    <MessagePreview :input="true" :message="sessionStore.replyMessage" :self="false"
                                    :session="sessionStore.selectedSession"/>
                </div>
            </Transition>

            <Transition
                enter-active-class="transition-all duration-300 ease-out"
                enter-from-class="opacity-0 translate-y-2"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition-all duration-200 ease-in"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-2"
            >
                <div
                    v-if="messageStore.messageTemplatesModal && sessionStore.selectedSession"
                    class="relative transition-all duration-200"
                >
                    <MessageTemplates/>
                </div>
            </Transition>

            <Teleport v-if="waapiStore.showCampaignModal === true" to="body">
                <WaapiCampaign/>
            </Teleport>

            <div
                v-if="store.status !== 'loading' && (sessionStore.selectedSession?.status === 'active' || sessionStore.type !== 'session')"
                :class="audioStore.isRecording ? ['justify-between'] : []"
                class="h-auto p-5 flex flex-row items-end"
            >
                <div
                    v-if="isWaapiSession && isExpired"
                    class="w-full h-full flex flex-row justify-center items-end">
                    <Button
                        v-if="canSendCampaign"
                        :loading="loading"
                        class="bg-indigo-500 text-white p-4 text-sm rounded-md cursor-pointer hover:bg-indigo-700 dark:bg-indigo-400 dark:hover:bg-indigo-300"
                        @click="waapiStore.showCampaignModal = true"
                    >
                        Enviar nova campanha
                    </Button>
                    <div v-else
                         class="text-gray-400 cursor-not-allowed dark:text-gray-500 bg-gray-50 p-4 text-sm rounded-md dark:bg-gray-700 ">
                        Você não possui permissão para reativar essa conversa
                    </div>
                </div>
                <EditInputArea v-else-if="sessionStore.inputMode === 'edit'"
                               :can-send="canSend"
                />
                <InputArea
                    v-else
                    ref="inputAreaRef"
                    :can-send="canSend"
                    @send-message="sendMessage"
                    @handle-keydown="handleKeydown"
                />
            </div>

            <div v-else class="h-auto w-full flex flex-row p-4 text-center flex flex-row items-center justify-center">
                <Button
                    :loading="loading"
                    class="bg-indigo-500 text-white p-4 text-sm rounded-md cursor-pointer hover:bg-indigo-700 dark:bg-indigo-400 dark:hover:bg-indigo-300"
                    @click="initSession()">
                    Iniciar Atendimento
                </Button>
            </div>
        </template>
        <Teleport v-if="sessionStore.openForwardSessionsModal" to="#modal-root">
            <ForwardSessionsModal
                :close-modal="sessionStore.closeForwardModal"
                :handle-confirm="handleConfirmForward"
                :open="sessionStore.openForwardSessionsModal"
            />
        </Teleport>
    </div>
</template>

<style>
input[placeholder="Search emoji"] {
    background: rgba(0, 0, 0, 0);
}

.v3-emoji-picker .v3-header {
    border-bottom: 0;
}

.v3-emoji-picker .v3-footer {
    border-top: 0;
}

.forward-bar-enter-active {
    transition: opacity 340ms ease,
    transform 340ms cubic-bezier(0.22, 1, 0.36, 1),
    filter 340ms ease;
}

.forward-bar-leave-active {
    transition: opacity 220ms ease,
    transform 220ms ease,
    filter 220ms ease;
}

.forward-bar-enter-from {
    opacity: 0;
    transform: translateY(18px) scale(0.985);
    filter: blur(3px);
}

.forward-bar-enter-to {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
}

.forward-bar-leave-from {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
}

.forward-bar-leave-to {
    opacity: 0;
    transform: translateY(10px) scale(0.985);
    filter: blur(2px);
}
</style>
